import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ScanText, Upload, Loader2, RotateCcw, CheckCircle2, XCircle } from "lucide-react";

const CAMPOS = [
  { key: "numero_pagare", label: "Número de pagaré" },
  { key: "deudor_nombre", label: "Deudor (nombre / razón social)" },
  { key: "ruc_ci", label: "RUC / C.I." },
  { key: "monto", label: "Monto" },
  { key: "moneda", label: "Moneda (USD / PYG)" },
  { key: "fecha_vencimiento", label: "Fecha de vencimiento (DD/MM/YYYY)" },
  { key: "fecha_emision", label: "Fecha de emisión (DD/MM/YYYY)" },
  { key: "lugar_pago", label: "Lugar de pago / ciudad" },
];

export default function AnalizarPagare({ open, onOpenChange }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [campos, setCampos] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview((p) => { if (p) URL.revokeObjectURL(p); return ""; });
    setCampos(null);
    setLoading(false);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Seleccioná una imagen (foto del pagaré)."); return; }
    if (f.size > 12 * 1024 * 1024) { toast.error("La imagen supera 12 MB. Sacá la foto con menor resolución."); return; }
    setFile(f);
    setCampos(null);
    setPreview((p) => { if (p) URL.revokeObjectURL(p); return URL.createObjectURL(f); });
  };

  const analizar = async () => {
    if (!file) { toast.error("Primero subí una foto del pagaré."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/documentos/analizar-pagare", fd);
      setCampos(r.data?.campos || {});
      toast.success("Documento analizado. Revisá los datos autocompletados.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "No se pudo analizar el documento.");
    } finally {
      setLoading(false);
    }
  };

  const setCampo = (k, v) => setCampos((c) => ({ ...(c || {}), [k]: v }));

  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="analizar-pagare-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-950">
            <ScanText className="h-5 w-5 text-emerald-700" /> Analizar pagaré con IA
          </DialogTitle>
          <DialogDescription>
            Subí una foto del pagaré o documento de deuda y Gemini extraerá los datos automáticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={onPick}
            data-testid="pagare-file-input"
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} data-testid="pagare-pick-button" className="border-slate-300">
              <Upload className="h-4 w-4 mr-1.5" /> {file ? "Cambiar foto" : "Subir foto"}
            </Button>
            <Button type="button" onClick={analizar} disabled={!file || loading} data-testid="pagare-analizar-button" className="bg-emerald-700 hover:bg-emerald-800">
              {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ScanText className="h-4 w-4 mr-1.5" />}
              {loading ? "Analizando…" : "Analizar documento"}
            </Button>
            {(file || campos) && (
              <Button type="button" variant="ghost" onClick={reset} data-testid="pagare-reset-button" className="text-slate-500">
                <RotateCcw className="h-4 w-4 mr-1.5" /> Limpiar
              </Button>
            )}
          </div>

          {preview && (
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
              <img src={preview} alt="Vista previa del pagaré" className="max-h-64 w-full object-contain" data-testid="pagare-preview" />
            </div>
          )}

          {campos && (
            <div className="space-y-3" data-testid="pagare-campos">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CAMPOS.map(({ key, label }) => (
                  <div key={key} className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">{label}</label>
                    <Input
                      value={campos[key] ?? ""}
                      onChange={(e) => setCampo(key, e.target.value)}
                      placeholder="—"
                      data-testid={`pagare-campo-${key}`}
                      className="h-9"
                    />
                  </div>
                ))}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-500">Firma visible</label>
                  <div className="flex items-center gap-2 h-9" data-testid="pagare-campo-firma_visible">
                    {campos.firma_visible
                      ? <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Sí</span>
                      : <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-400"><XCircle className="h-4 w-4" /> No</span>}
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-slate-400">
                Los datos son extraídos por IA y pueden requerir verificación manual. Podés editarlos arriba.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
